import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { RichText } from "@/components/site/RichText";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ServiceBand } from "@/components/site/ServiceBand";
import { ProcessSteps } from "@/components/site/ProcessSteps";
import { AlertTriangle, Layers, Hammer, Settings, ArrowRight, ShieldCheck } from "lucide-react";
import renovatieHero from "../../assets/renovatie-hero.avif";
import renovatieHeroSmall from "../../assets/renovatie-hero-640.avif";
import { FaqSection } from "@/components/site/FaqSection";
import { faqs, faqJsonLd } from "@/content/faqs";
import { canonical, breadcrumbJsonLd, serviceJsonLd, shareImageMeta, langPageMeta } from "@/content/site";
import { responseTime } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/renovatie";
const SLEUTEL = "renovatie";

const HERO_FALLBACK = { src: renovatieHero, srcSet: `${renovatieHeroSmall} 640w, ${renovatieHero} 1024w` };

const CODE_META_TITLE = "Renovatie polyester monoblock zwembad | Limburg";
const CODE_META_DESCRIPTION =
  "Specialist in renovatie van polyester monoblock zwembaden met osmose, verkleuring of lekkage. Ook bouwkundige baden, leidingwerk en afdekkingen. Limburg en Zuid-Nederland.";

export const Route = createFileRoute("/$lang/renovatie")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => ({
    page: await context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
  }),
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), HERO_FALLBACK);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        {
          property: "og:description",
          content: "Uw versleten polyester zwembad weer als nieuw. Osmose, verkleuring en lekkages zijn onze specialiteit.",
        },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("renovatie", "Renovatie van een bestaand zwembad door Gopro Projects"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [
        faqJsonLd(faqs.renovatie),
        breadcrumbJsonLd("Renovatie monoblock zwembaden", PATH),
        serviceJsonLd({
          name: "Renovatie van polyester monoblock en bouwkundige zwembaden",
          serviceType: "Zwembadrenovatie",
          description:
            "Relining van polyester monoblock zwembaden met osmose, blaasvorming of verkleuring, en renovatie van bouwkundige baden inclusief leidingwerk, isolatie, filtratie en afdekking.",
          path: PATH,
        }),
      ],
    };
  },
  component: RenovatiePage,
});

const problems = [
  {
    titel: "Osmose & blaasvorming",
    tekst:
      "Oudere polyester kuipen zuigen vocht op, waardoor blaren en loslatende lagen ontstaan. Wij verwijderen de beschadigde lagen en brengen een nieuwe, waterdichte afwerking aan.",
  },
  {
    titel: "Scheuren & lekkage",
    tekst:
      "Beweging in de grond en temperatuurswisselingen zorgen voor haarscheurtjes. Onze relining-techniek herstelt de structuur en maakt de kuip opnieuw volledig dicht.",
  },
  {
    titel: "Verkleuring & veroudering",
    tekst:
      "UV-straling en jarenlang gebruik maken de oppervlakte dof en verkleurd. Wij geven uw bad een nieuwe, kleurvaste laag in de kleur van uw keuze.",
  },
  {
    titel: "Klimaatgevoeligheid",
    tekst:
      "Polyester reageert sterk op warmte en kou. Onze renovatie gebruikt materialen die beter bestand zijn tegen temperatuurschommelingen, voor een langere levensduur.",
  },
];

const solutionIcons = [Layers, Hammer, Settings];

const solutions = [
  {
    titel: "Monoblock relining",
    tekst:
      "Een nieuwe laag binnenin uw bestaande polyester kuip: sneller, voordeliger en minder ingrijpend dan vervanging. Deze renovaties vormen ongeveer de helft van onze activiteiten.",
  },
  {
    titel: "Renovatie bouwkundige baden",
    tekst:
      "Bestaande betonbaden krijgen een tweede leven met nieuwe bekleding, isolatie, boordstenen en een technische upgrade.",
  },
  {
    titel: "Technische & wellness upgrades",
    tekst: "Nieuwe filtratie, warmtepomp, verlichting of tegenstroom: wij moderniseren de techniek achter het bad.",
  },
];

const structuralRenovation = [
  "Vernieuwen van leidingwerk",
  "Vernieuwen van zwembadbekleding in combinatie met isolatie",
  "Plaatsen van nieuwe filtratie",
  "Plaatsen van zwembadafdekkingen",
  "Winter- en zomeronderhoud",
];

const renovationSteps = [
  {
    titel: "1. Diagnose met foto's of ter plaatse",
    tekst:
      "Wij beoordelen de aantasting, de staat van de trap, de doorvoeren en het leidingwerk, en bepalen of relining volstaat of er meer nodig is.",
  },
  {
    titel: "2. Leegmaken en reinigen",
    tekst:
      "Het bad wordt leeggemaakt, grondig gereinigd en volledig droog gezet. Werken gebeurt bij voorkeur onder een werftent zodat vocht en stof geen kans krijgen.",
  },
  {
    titel: "3. Wegnemen van de aangetaste lagen",
    tekst:
      "Blaasvorming, losse polyester en de oude topcoat worden weggeschuurd tot op een gezonde, draagkrachtige ondergrond.",
  },
  {
    titel: "4. Herstel van scheuren en doorvoeren",
    tekst:
      "Haarscheuren, skimmers, inspuiters en spots worden hersteld of vervangen. Waar nodig vernieuwen wij meteen het leidingwerk.",
  },
  {
    titel: "5. Nieuwe laminaten en topcoat",
    tekst:
      "De kuip krijgt nieuwe lagen polyester of het osmosebestendigere vinylester, gevolgd door schuren en een kleurvaste topcoat in de kleur van uw keuze.",
  },
  {
    titel: "6. Technieken, vullen en opstart",
    tekst:
      "Filtratie, verlichting, warmtepomp of rolluik worden mee aangepakt, het bad wordt gevuld en de waterbalans wordt correct ingesteld.",
  },
];

function RenovatiePage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const hero = c.section("hero");
  const heroPhoto = heroDelivery(hero, HERO_FALLBACK);

  return (
    <div className="flex flex-col">
      <PageHero
        title={c.veld("hero", "titel", "Renovatie van polyester monoblock zwembaden")}
        subtitle={c.rijkInline("hero",
          "Waar andere bedrijven afhaken, komen wij juist in actie. De renovatie van monoblock zwembaden — osmose, verkleuring en lekkages — is onze grootste specialiteit en vormt ongeveer de helft van onze activiteiten. Wij geven versleten, lekkende of beschadigde polyester zwembaden een volledig tweede leven.",
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageAlt={c.veld("hero", "alt", "Voor en na renovatie van een polyester monoblock zwembad in Limburg")}
        cta={{
          label: c.veld("hero", "knopTekst", "Vraag diagnose aan"),
          to: c.veld("hero", "knopLink", "/offerte") as PublicPath,
        }}
        secondaryCta={{
          label: c.veld("hero", "knop2Tekst", "Bekijk realisaties"),
          to: c.veld("hero", "knop2Link", "/realisaties") as PublicPath,
        }}
      />

      <Section tone="light">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("osmose", "overline", "Osmose herkennen")}</Overline>
          <h2 className="mt-4 display-section">{c.veld("osmose", "titel", "Hoe herkent u osmose in uw zwembad?")}</h2>
          {c
            .alineas("osmose", [
              "Osmose herkent u aan kleine blaasjes of bobbels in de polyesterlaag, een ruw of krijtachtig oppervlak, verkleurde plekken en soms een lichte gelige waas in het water. Drukt u een blaasje open, dan komt er vaak een zurig ruikend vocht uit.",
              "Osmose ontstaat doordat water jarenlang heel traag door de toplaag dringt en zich ophoopt tussen de lagen polyester. Dat is geen schoonheidsfoutje: als de aangetaste lagen niet worden weggenomen, gaat de aantasting verder en laat de bekleding op termijn los. Een verkleurde, doffe of ruwe kuip zonder blaasjes is meestal een topcoatprobleem — ook dat lossen wij op, en dan blijft de ingreep beperkt.",
              "Twijfelt u? Stuur ons enkele foto's van het droge bad, van de trap en van een close-up van de aantasting. Wij zeggen eerlijk of renoveren zinvol is — ook wanneer dat betekent dat wachten of een andere aanpak beter uitkomt.",
            ])
            .map((alinea, index) => (
              <p
                key={alinea.slice(0, 24)}
                className={`${index === 0 ? "mt-6" : "mt-4"} text-[17px] leading-7 text-on-surface-variant`}
              >
                <RichText waarde={alinea} inline />
              </p>
            ))}
        </div>
      </Section>

      <ProcessSteps
        overline={c.veld("proces", "overline", "Werkwijze")}
        title={c.veld("proces", "titel", "Zo verloopt een renovatie")}
        intro={c.rijkInline("proces",
          "Een renovatie duurt doorgaans één tot enkele weken en verloopt in vaste stappen. Uw tuin blijft daarbij grotendeels intact — er wordt niets uitgebroken.",
        )}
        steps={c.kaarten("proces", renovationSteps).map((stap) => ({ title: stap.titel, text: stap.tekst }))}
      />

      <Section tone="mist">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("afweging", "overline", "Afweging")}</Overline>
          <h3 className="mt-4 font-display text-xl font-semibold">
            {c.veld("afweging", "titel", "Renoveren of vervangen?")}
          </h3>
          <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("afweging",
              "Een monoblock kuip uitbreken betekent zwaar machinewerk, een opengebroken tuin en een volledig nieuw bad. Relining gebeurt binnenin de bestaande kuip: uw terras, boordstenen en tuinaanleg blijven liggen, het werk duurt korter en de kost ligt merkelijk lager. Na renovatie is de kuip opnieuw naadloos waterdicht en gaat ze weer vele jaren mee — met vinylester als extra osmosebestendige afwerking.",
            )}
          </p>
        </div>
      </Section>

      <Section tone="light">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("niche", "overline", "Onze niche")}</Overline>
          <h2 className="mt-4 display-section">
            {c.veld("niche", "titel", "Waarom weigeren andere bedrijven polyester kuipen?")}
          </h2>
          <p className="mt-6 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("niche", "Deze problemen maken renovatie arbeidsintensief. Juist daarom is het onze niche.")}
          </p>
        </div>
        <div className="mt-12 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
          {c.kaarten("niche", problems).map((problem) => (
            <article key={problem.titel} className="bg-background p-6">
              <AlertTriangle className="h-11 w-11 text-brand-deep-water" strokeWidth={1.75} />
              <h3 className="mt-4 font-display text-lg font-semibold">{problem.titel}</h3>
              <p className="mt-2 text-sm leading-6 text-on-surface-variant">{problem.tekst}</p>
            </article>
          ))}
        </div>
      </Section>

      <ServiceBand
        items={c.kaarten("oplossingen", solutions).map((kaart, index) => ({
          icon: solutionIcons[index % solutionIcons.length]!,
          title: kaart.titel,
          text: kaart.tekst,
        }))}
      />

      <Section tone="mist">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Overline>{c.veld("bouwkundig", "overline", "Bouwkundige baden")}</Overline>
            <h2 className="mt-4 display-section">
              {c.veld("bouwkundig", "titel", "Renoveren van bouwkundige baden")}
            </h2>
            <p className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("bouwkundig",
                "Ook bestaande bouwkundige zwembaden krijgen bij ons een volledige make-over. Wij combineren nieuwe bekleding met isolatie, vernieuwen techniek en zorgen voor een strakke afwerking.",
              )}
            </p>
            <ul className="mt-8 divide-y divide-border border-y border-border text-on-surface-variant">
              {c.lijst("bouwkundig", structuralRenovation).map((item) => (
                <li key={item} className="flex items-start gap-3 py-4">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-deep-water" strokeWidth={1.75} />
                  <span className="text-sm leading-6">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-primary p-8 text-primary-foreground lg:p-10">
            <Overline onDark>{c.veld("nazorg", "overline", "Nazorg")}</Overline>
            <h3 className="mt-4 font-display text-xl font-semibold">
              {c.veld("nazorg", "titel", "Winter- en zomeronderhoud")}
            </h3>
            <p className="mt-4 text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("nazorg",
                "Wilt u zorgeloos genieten van uw zwembad? Wij bieden onderhoudscontracten aan voor winterklaar maken, opstarten in het voorjaar en regelmatige nazorg.",
              )}
            </p>
          </div>
        </div>
      </Section>

      <section className="bg-primary py-16 text-primary-foreground lg:py-20">
        <div className="mx-auto w-full max-w-[1280px] px-5 lg:px-16">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
            <div>
              <Overline onDark>{c.veld("cta", "overline", "Eerlijk advies")}</Overline>
              <h2 className="mt-4 display-section">
                {c.veld("cta", "titel", "Twijfelt u of uw bad nog te redden is?")}
              </h2>
              <p className="mt-4 max-w-[70ch] text-[17px] leading-7 text-primary-foreground/80">
                {c.rijkInline("cta",
                  `Ook oudere, verkleurde of osmose-baden zijn vaak nog te renoveren. Stuur foto's door via ons diagnoseformulier. Wij geven ${responseTime.within} eerlijk advies.`,
                )}
              </p>
            </div>
            <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <LocaleLink to={c.veld("cta", "knopLink", "/offerte") as PublicPath}>
                {c.veld("cta", "knopTekst", "Diagnose starten")} <ArrowRight className="ml-2 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>
        </div>
      </section>
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.renovatie} />
    </div>
  );
}
